"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, MapPin, Users, Target, ArrowRight, CircleCheck as CheckCircle2, ChartBar as BarChart3, Sparkles, Zap, Building2, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
const chartData = [{
  sector: "B2B & Industrial",
  searchVolume: 85,
  growth: 34
}, {
  sector: "Professional Services",
  searchVolume: 92,
  growth: 41
}, {
  sector: "Trades & Home Services",
  searchVolume: 98,
  growth: 48
}, {
  sector: "Healthcare & Tech",
  searchVolume: 76,
  growth: 29
}, {
  sector: "Retail & Ecommerce",
  searchVolume: 88,
  growth: 38
}];
const chartConfig = {
  searchVolume: {
    label: "Local Intent Demand",
    color: "hsl(220 80% 30%)"
  },
  growth: {
    label: "YoY Search Growth (%)",
    color: "hsl(340 75% 65%)"
  }
} satisfies ChartConfig;
const marketInsights = [{
  icon: Search,
  title: "High Commercial Intent",
  description: "Over 81% of local Telford searches for services (e.g., 'B2B consultant Telford', 'engineering suppliers Telford') carry immediate purchasing intent."
}, {
  icon: Target,
  title: "Under-Optimized Competitors",
  description: "More than 65% of regional Telford businesses rely on outdated agency tactics, leaving prime Google Map Pack positions open for strategic capture."
}, {
  icon: TrendingUp,
  title: "Rapid Regional Digital Growth",
  description: "With Telford's tech and manufacturing sectors expanding, digital search volume across Shropshire has increased by 42% over the last 18 months."
}, {
  icon: Users,
  title: "Direct Specialist Advantage",
  description: "Working directly with me means no agency account manager games. I execute bespoke strategies aligned strictly with Telford's buyer demographics."
}];
const behaviorPillars = [{
  id: "near-me",
  badge: "Localized Demand",
  title: "Hyper-Local 'Near Me' Searches",
  content: "Telford buyers favor local credibility. My technical local SEO ensures your business dominates geo-targeted search results and Google Maps listings across SN5 and Shropshire.",
  stats: "78% mobile local search conversions within 24h"
}, {
  id: "b2b-lead",
  badge: "Industrial & Commercial",
  title: "B2B Decision Maker Behavior",
  content: "Engineers, procurement managers, and business owners in Telford conduct thorough organic research before reaching out. I align your organic funnel to convert high-value buyers.",
  stats: "3.4x higher lead quality vs paid social ads"
}, {
  id: "ai-search",
  badge: "Modern AI Discovery",
  title: "Google AI Overviews & ChatGPT",
  content: "Local buyers increasingly ask AI engines for regional recommendations. I structure your digital presence to get cited directly in AI-generated answers.",
  stats: "Top 3 inclusion rate in regional AI citations"
}];
export default function TelfordMarket() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState(0);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="telfordmarketlandscape" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors duration-300 relative overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full border border-accent/20">
            <MapPin className="w-3.5 h-3.5 text-accent mr-1" /> Telford Search Intelligence
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight leading-tight">
            Telford Search Landscape & Market Opportunity
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            How regional buyer behavior, growing industrial demand, and uncaptured local search positions create a high-converting growth window for Telford businesses working directly with me.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketInsights.map((insight, i) => {
          const Icon = insight.icon;
          return <motion.div key={insight.title} variants={itemVariants} data-index={i}>
                <Card className="bg-card text-card-foreground border border-border shadow-xs hover:border-accent/50 transition-[border-color,box-shadow] duration-300 h-full flex flex-col justify-between">
                  <CardHeader className="p-6 pb-2">
                    <div className="w-12 h-12 rounded-xl bg-secondary/60 text-accent flex items-center justify-center mb-4 border border-accent/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-lg font-bold font-serif text-foreground">
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-2">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {insight.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16">
          <div className="lg:col-span-5 space-y-6">
            <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Quantified Regional Opportunity</span>
            </div>
            
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-primary leading-snug">
              Capturing High-Value Local Searches in Telford & Shropshire
            </h3>

            <p className="text-muted-foreground text-base leading-relaxed">
              Telford's economy is uniquely driven by advanced manufacturing, logistics, tech hubs, and commercial services. When local buyers search for high-value services, they look for authoritative, regionally trusted providers.
            </p>

            <ul className="space-y-3 pt-2">
              {["Targeted ranking strategies for local B2B and consumer intent keywords", "Google Business Profile optimization to capture local Map Pack clicks", "Conversion-focused copy that transforms organic traffic into consultation calls", "Direct consultant execution — no junior agency team handoffs"].map((point, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>)}
            </ul>

            <div className="pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm rounded-md">
                <Link to="/ContactPage">
                  <span>Book a Free Telford SEO Audit</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="bg-card border border-border shadow-md overflow-hidden">
              <CardHeader className="p-6 pb-2 border-b border-border bg-muted/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-bold font-serif text-foreground flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      Telford Commercial Search Volume Growth by Sector
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground">
                      Index of regional search demand vs annual growth rate
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="w-fit text-accent border-accent/40 bg-secondary/30">
                    Active Regional Index
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-6">
                <ChartContainer config={chartConfig} className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0
                  }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                      <XAxis dataKey="sector" tickLine={false} axisLine={false} tick={{
                      fontSize: 12.5,
                      fill: "var(--color-muted-foreground)"
                    }} />
                      <YAxis tickLine={false} axisLine={false} tick={{
                      fontSize: 12.5,
                      fill: "var(--color-muted-foreground)"
                    }} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="searchVolume" fill="var(--color-searchVolume)" radius={[4, 4, 0, 0]} name="Search Demand Index" />
                      <Bar dataKey="growth" fill="var(--color-growth)" radius={[4, 4, 0, 0]} name="YoY Growth %" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-xs bg-primary inline-block" /> Search Demand
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-xs bg-accent inline-block" /> YoY Growth %
                  </span>
                  <span className="text-foreground font-medium">Source: Regional Search Trends</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 md:p-10 shadow-xs mb-16">
          <div className="max-w-2xl mb-8">
            <Badge variant="outline" className="mb-3 text-primary border-primary/30">
              Consumer & B2B Insights
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-primary">
              How Telford Buyers Search & Choose Service Providers
            </h3>
            <p className="text-muted-foreground text-sm md:text-base mt-2">
              Understanding search intent is the foundation of sustainable search growth. Here is how local buyers navigate organic search in Shropshire.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {behaviorPillars.map((pillar, idx) => <div key={pillar.id} data-index={idx} className="bg-background border border-border rounded-lg p-6 flex flex-col justify-between hover:border-accent/40 transition-[border-color] duration-300">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-semibold px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full border border-accent/20">
                      {pillar.badge}
                    </span>
                    <Zap className="w-4 h-4 text-accent" />
                  </div>
                  <h4 className="text-lg font-bold font-serif text-foreground mb-2">
                    {pillar.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {pillar.content}
                  </p>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-accent" />
                    <span>{pillar.stats}</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="bg-secondary/40 border border-accent/30 rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto relative overflow-hidden">
          <div className="absolute -top-12 -left-12 w-32 h-32 bg-accent/10 rounded-full blur-xl pointer-events-none" />
          <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-primary/10 rounded-full blur-xl pointer-events-none" />

          <div className="relative z-10 space-y-6">
            <div className="inline-flex items-center gap-2 bg-background px-3 py-1 rounded-full text-xs font-semibold text-primary border border-border shadow-2xs">
              <Building2 className="w-3.5 h-3.5 text-accent" /> Direct Specialist Access
            </div>

            <h3 className="text-2xl md:text-3xl font-bold font-serif text-primary leading-tight">
              Ready to Dominate Local Search in Telford?
            </h3>

            <p className="text-foreground text-base max-w-2xl mx-auto leading-relaxed">
              You work directly with me — Yassine Essaadi. No account managers, no automated template reporting, and no hidden handoffs. Just tailor-made SEO strategy designed to grow your business in Telford and beyond.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md w-full sm:w-auto">
                <Link to="/ContactPage">
                  <PhoneCall className="w-4 h-4 mr-2" />
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-background w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  <span>Explore Genuine Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}